/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import axios, {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { forceRenewToken } from './requestHelper';

/**
 * Global 401 response interceptor: silently renew the token and retry the
 * original request instead of surfacing the error / bouncing to /login.
 *
 * The proactive refresh (useAutoRefreshToken timer + getAccessToken pre-expiry
 * check) prevents most expiries, but a token can still be rejected mid-session
 * (server-side revoke, clock skew, a route that missed the proactive path).
 * Without this, such a 401 just fails the request. Here we catch it, renew via
 * forceRenewToken (refresh_token grant → silent re-auth → sign out), and replay
 * the request once with the fresh token so the user never notices.
 *
 * Attached to the default axios instance, which the insight-service SDK's
 * BaseAPI uses by default (globalAxios) and which requestHelper's makeRequest
 * uses too — so this single interceptor covers every API call.
 */

// Requests we must NOT retry-on-401 (the token endpoints themselves): retrying
// them would recurse. A 401/400 from these is a real auth failure.
const isAuthEndpoint = (url?: string): boolean =>
  !!url && (url.includes('/public') || url.includes('grantToken'));

let installed = false;

export const installAuthInterceptor = (): void => {
  if (installed) return;
  installed = true;

  axios.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
      const original = error.config as
        | (InternalAxiosRequestConfig & { _retriedAfter401?: boolean })
        | undefined;

      const status = error.response?.status;
      if (
        status !== 401 ||
        !original ||
        original._retriedAfter401 ||
        isAuthEndpoint(original.url)
      ) {
        return Promise.reject(error);
      }

      original._retriedAfter401 = true;

      const freshToken = await forceRenewToken('id');
      if (!freshToken) {
        // Renewal failed; forceRenewToken already signed out. Let the original
        // 401 propagate so callers/usePrivateRoute handle the logout.
        return Promise.reject(error);
      }

      // Replay the original request with the new token. On an
      // InternalAxiosRequestConfig `headers` is always an AxiosHeaders instance,
      // so we can set the field directly.
      original.headers.set('Authorization', `Bearer ${freshToken}`);
      return axios(original);
    },
  );
};
